import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthService } from '../lib/services/AuthService';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import { EMAIL_CONFIG } from '../config/constants';
import Logo from '../logo.svg';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { signUpWithEmail, resendVerificationEmail } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [lastRegisteredEmail, setLastRegisteredEmail] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Fonction de validation du mot de passe avec règles de sécurité
  const validatePassword = (pwd: string): boolean => {
    return (
      pwd.length >= 8 &&
      /[A-Z]/.test(pwd) &&
      /[a-z]/.test(pwd) &&
      /[0-9]/.test(pwd) &&
      /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!email || !password || !confirmPassword) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    // Validation du mot de passe avec règles de sécurité
    if (!validatePassword(password)) {
      setError('Le mot de passe ne respecte pas les règles de sécurité');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (!acceptedTerms) {
      setError('Vous devez accepter les conditions générales d\'utilisation pour continuer');
      return;
    }

    try {
      setSubmitting(true);
      const result = await signUpWithEmail(email, password);
      
      // Appeler init_user_defaults si l'utilisateur est créé
      if (result?.user?.id) {
        try {
          // Marquer l'acceptation des conditions générales
          await AuthService.acceptTerms(result.user.id);
          
          console.log('Initialisation des données par défaut pour le nouvel utilisateur...');
          const { error: initError } = await AuthService.initUserDefaults(result.user.id);
          
          if (initError) {
            console.warn('Erreur lors de l\'initialisation des données par défaut:', initError);
            // On continue quand même, ce n'est pas bloquant
          } else {
            console.log('Données par défaut initialisées avec succès');
          }
        } catch (initErr: any) {
          console.warn('Erreur lors de l\'initialisation des données par défaut:', initErr);
          // On continue quand même, ce n'est pas bloquant
        }
      }
      
      setSuccessMsg("Inscription réussie. Vérifiez votre email pour confirmer votre compte.");
      setLastRegisteredEmail(email);
      // Optionnel: rediriger vers login après quelques secondes
      setTimeout(() => navigate('/login'), 2000);
    } catch (e: any) {
      setError(e?.message || "Erreur lors de l'inscription");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <img src={Logo} alt="Logo" className="w-20 h-20" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            Créer un compte
          </h2>
          <p className="text-dark-400">
            Rejoignez SuiviFinance pour gérer vos finances
          </p>
        </div>

        <div className="bg-dark-800 rounded-lg p-6 shadow-xl">
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}
          {successMsg && (
            <div className="mb-4 p-3 bg-green-500/20 border border-green-500 rounded-lg flex items-center space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-green-400 text-sm">{successMsg}</span>
            </div>
          )}

          {lastRegisteredEmail && (
            <div className="mb-4 text-center">
              <button
                type="button"
                onClick={async () => {
                  try {
                    setSubmitting(true);
                    await resendVerificationEmail(lastRegisteredEmail);
                    setSuccessMsg('Email de vérification renvoyé. Vérifiez votre boîte de réception.');
                  } catch (e: any) {
                    setError(e?.message || "Impossible de renvoyer l'email de vérification");
                  } finally {
                    setSubmitting(false);
                  }
                }}
                className="px-3 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors text-sm"
                disabled={submitting}
              >
                Renvoyer l'email de confirmation
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-dark-400 mb-2">Adresse email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-dark-500" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={EMAIL_CONFIG.PLACEHOLDER}
                  disabled={submitting}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-dark-400 mb-2">Mot de passe</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-dark-500" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Votre mot de passe"
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-dark-400 hover:text-white transition-colors"
                  disabled={submitting}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {/* Affichage des règles de sécurité du mot de passe */}
              {password.length > 0 && (
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-dark-400 mb-1">Le mot de passe doit contenir :</p>
                  <ul className="text-xs space-y-1">
                    <li className={`flex items-center space-x-2 ${password.length >= 8 ? 'text-green-400' : 'text-dark-400'}`}>
                      <span>{password.length >= 8 ? '✓' : '○'}</span>
                      <span>Au moins 8 caractères</span>
                    </li>
                    <li className={`flex items-center space-x-2 ${/[A-Z]/.test(password) ? 'text-green-400' : 'text-dark-400'}`}>
                      <span>{/[A-Z]/.test(password) ? '✓' : '○'}</span>
                      <span>Au moins une majuscule</span>
                    </li>
                    <li className={`flex items-center space-x-2 ${/[a-z]/.test(password) ? 'text-green-400' : 'text-dark-400'}`}>
                      <span>{/[a-z]/.test(password) ? '✓' : '○'}</span>
                      <span>Au moins une minuscule</span>
                    </li>
                    <li className={`flex items-center space-x-2 ${/[0-9]/.test(password) ? 'text-green-400' : 'text-dark-400'}`}>
                      <span>{/[0-9]/.test(password) ? '✓' : '○'}</span>
                      <span>Au moins un chiffre</span>
                    </li>
                    <li className={`flex items-center space-x-2 ${/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) ? 'text-green-400' : 'text-dark-400'}`}>
                      <span>{/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) ? '✓' : '○'}</span>
                      <span>Au moins un caractère spécial</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-dark-400 mb-2">Confirmer le mot de passe</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-dark-500" />
                </div>
                <input
                  id="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Confirmez le mot de passe"
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-dark-400 hover:text-white transition-colors"
                  disabled={submitting}
                >
                  {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Checkbox d'acceptation des conditions */}
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-4 h-4 text-primary-500 bg-dark-700 border-dark-600 rounded focus:ring-primary-500 focus:ring-2"
                disabled={submitting}
              />
              <label htmlFor="acceptTerms" className="text-sm text-dark-400">
                J'accepte les{' '}
                <Link to="/terms" target="_blank" className="text-primary-400 hover:text-primary-300 underline">
                  conditions d'utilisation
                </Link>
                {' '}et la{' '}
                <Link to="/privacy" target="_blank" className="text-primary-400 hover:text-primary-300 underline">
                  politique de confidentialité
                </Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting || !acceptedTerms}
              className="w-full px-4 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
            >
              {submitting ? 'Création du compte…' : "S'inscrire"}
            </button>
          </form>
        </div>

        <div className="text-center">
          <div className="text-sm text-dark-400">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-primary-400 hover:text-primary-300 transition-colors duration-200 font-medium">
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
